import {
  Component,
  Event,
  type EventEmitter,
  h,
  Host,
  Prop,
  Listen,
  Element,
  State,
} from '@stencil/core';
import type {TabNavbarClicked} from '../tabs-header/TabNavbarClicked';
import {SearchTab} from '../search-tab/search-tab';
import type {TabAnalyticsEvent} from '../tabs-header/TabAnalyticsEvent';
import {makeLogger} from '../../../logger/logger';
import type {DashboardTabViewV2} from '../search-tab/subcomponents/dashboard-tab-view/dashboard-tab-view-v2';
import {getSiteSearchCategory} from '../../analytics-reporting/util';
import type {labelProviderInterface} from '../../LabelProviderInterface';

interface IQdsTabBar {
  disableEndArrow: boolean;
  scrollWidth: number;
  clientWidth: number;
}

const {warn} = makeLogger({
  pathToCurrentFile: 'components/result-page/tabs-header/search-tabs-nav-header-v2.tsx',
  loggersList: ['warn']
});

@Component({
  tag: 'search-tabs-nav-header-v2',
  styleUrl: 'search-tabs-nav-header-v2.scss',
})
export class SearchTabsNavHeaderV2 {
  @Prop() readonly tabs;
  @Prop() readonly tabsReflected;
  @Prop() readonly query: string;

  @Prop() readonly rtl: boolean;
  @Prop() readonly tabCounts: boolean = false;
  @Event() tabClicked: EventEmitter<TabNavbarClicked>;
  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @State() isViewAllEvent = false;
  @Prop() readonly active = 0;
  @Element() private hostElement: HTMLElement;


  private tabElements: HTMLElement[] = [];
  private tabBarElement: IQdsTabBar;

  @Event({
    eventName: 'search:tabClick',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    tabInteractionEventEmmiter: EventEmitter<TabAnalyticsEvent>;


  @Listen('search:dashboardViewAll', {target: 'document'})
  handleViewAllClick(event) {
      this.isViewAllEvent = event.detail?.isViewAllClicked;
      const tabName = event.detail?.tabName;
      const selectedTabElement = this.hostElement.querySelector(`[data-tab-name="${tabName}"]`);
      selectedTabElement.scrollIntoView();
  }


  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            warn(
              'Label provider not defined for the Search Tab Nav component ',
              this,
              [name, fallback]
            );
            return fallback;
        };
      }
  }

  componentWillUpdate() {
      if (this.tabElements.length > 0) {
        this.tabElements.forEach((tab, index) => {
            if (this.active === index && !tab.hasAttribute('selected')) {
              tab.setAttribute('selected', 'true');
              (tab as HTMLElement).click();
            }
        });
      }
      if (this.tabBarElement) {
        if(this.tabBarElement.clientWidth <= this.tabBarElement.scrollWidth) {
          this.tabBarElement.disableEndArrow = false;
        }
      }
  }

  render() {
      return (
        <Host>
          {
            this.tabs && this.tabs.length > 0 && (
              <div class="tabs__container" hidden={this.tabs.length === 1}>
                <qds-tabbar
                  onQdsTabShow={(e) => this.showSelectedTab(e.detail.panel, e.detail.panel.tabNumber)} layer="main"
                  level="root" platform="desktop" orientation="horizontal" ref={el=> this.tabBarElement = el}
                >
                  {
                    this.tabs.map(((tab) => {
                        const tabContent = this.tabsReflected[tab.tabName];
                        const numberOfResults = tab.tabName === 'se/all' ? false : tabContent?.count;
                        return (
                          <qds-tab
                            panel={tab}
                            text={this.getTabName(tab).replace(/\s/g, '\u00A0')}
                            badge={numberOfResults}
                            orientation={'horizontal'}
                            data-tab-name={tab.tabName}
                            ref={el => {
                                this.tabElements.push(el as HTMLElement);
                            }}
                          >
                          </qds-tab>
                        );
                    }))
                  }
                </qds-tabbar>
              </div>
            )
          }
        </Host>
      );
  }

  private getTabName(searchTab: SearchTab | DashboardTabViewV2) {
      return this.labelProvider(
        'tab.' + searchTab.tabName,
        searchTab?.tabTitle || searchTab.tabName
      );
  }


  private showSelectedTab(tab: any, num: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
      this.tabClicked.emit({
        tab: tab,
        num: num,
      });

      const tabC = this.tabsReflected[tab.tabName];

      this.tabInteractionEventEmmiter.emit({
        event: 'tabClick',
        tabName: getSiteSearchCategory(tab.tabName, tab.appName),
        isHidden: false,
        position: num,
        tabCount: tabC?.count,
        category: this.isViewAllEvent ? 'tab-nav-view-all' : 'tab-nav',
        keyword: this.query
      });
  }
}
